import { Button } from '@/components/ui/button';
import type { RouterContext } from '@/lib/router-context';
import { useSession } from '@/lib/use-session';
import { Link, Outlet, createRootRouteWithContext, useNavigate } from '@tanstack/react-router';
import { LogOutIcon } from 'lucide-react';

/**
 * Root layout shared by every route.
 *
 * Uses `createRootRouteWithContext` so child routes' `beforeLoad` guards
 * can reach into the typed router context (currently just `queryClient`).
 *
 * The header is session-aware: unauthenticated visitors see a minimal
 * brand bar; logged-in users see the primary nav (Hills / Archive), their
 * name, avatar and a logout button. We don't hide the header entirely on
 * /login because the brand anchor is still useful context.
 */
export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
});

/**
 * Exported for testing. The router only needs `Route`, but tests want to
 * render the layout directly without standing up a RouterProvider and
 * a Transitioner dance - so we expose the function too.
 */
export function RootComponent() {
  const { user, logout } = useSession();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    await navigate({ to: '/login' });
  };

  return (
    <div className="mx-auto flex min-h-screen max-w-6xl flex-col px-6">
      {/*
        PMS brand lockup: the logo is the gradient ring from the 2018
        charter; the wordmark pairs "hills" (bold) with " . pms" (thin)
        to echo the "patrickmathieu / singularité" contrast used across
        the charter. The anchor stays a real link (-> `/`) so middle-click
        and copy-link behave; the Link component handles SPA navigation.
      */}
      <header className="sticky top-0 z-10 flex items-center gap-4 border-slate-200 border-b bg-white py-3">
        <Link
          to="/"
          className="flex items-center gap-2.5 text-brand-900 no-underline hover:no-underline"
        >
          <img src="/logo.png" alt="" className="h-7 w-7" />
          <span className="inline-flex items-baseline leading-none">
            <span className="font-bold text-base tracking-tight">hills</span>
            <span className="ml-[2px] font-extralight text-base tracking-tight">{' · pms'}</span>
          </span>
        </Link>
        {/*
          Top-level nav: Hills (live work) + Archive (Done hills). Only
          shown to authenticated users - the unauth header keeps the brand
          anchor alone for /login. The active link gets a subtle background
          via TanStack Router's `activeProps`.
        */}
        {user ? (
          <nav aria-label="Primary" className="flex items-center gap-1 text-sm">
            <Link
              to="/hills"
              activeProps={{ className: 'bg-brand-50 text-brand-900' }}
              className="rounded px-2.5 py-1 text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
            >
              Hills
            </Link>
            <Link
              to="/archive"
              activeProps={{ className: 'bg-brand-50 text-brand-900' }}
              className="rounded px-2.5 py-1 text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
            >
              Archive
            </Link>
          </nav>
        ) : null}
        <div className="ml-auto flex items-center gap-3">
          {user ? (
            <>
              <div className="flex items-center gap-2 text-sm">
                {user.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt=""
                    className="h-7 w-7 rounded-full"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-50 font-bold text-[11px] text-brand-900">
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <span className="font-medium">{user.name}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={handleLogout} aria-label="Sign out">
                <LogOutIcon className="h-4 w-4" />
                Sign out
              </Button>
            </>
          ) : null}
        </div>
      </header>
      <main className="flex-1 py-6">
        <Outlet />
      </main>
    </div>
  );
}
